import { cn } from "@/lib/utils";
import type { HTMLAttributes, ReactNode } from "react";

export interface MCardProps extends HTMLAttributes<HTMLDivElement> {
  flush?: boolean;
}

export function MCard({ className, flush, ...rest }: MCardProps) {
  return (
    <div
      className={cn("m-card", flush ? "" : "p-4", className)}
      {...rest}
    />
  );
}

export interface MTileProps extends HTMLAttributes<HTMLButtonElement> {
  tone?: "default" | "warn" | "crit";
  value: ReactNode;
  label: ReactNode;
  icon?: ReactNode;
}

export function MTile({
  tone = "default",
  value,
  label,
  icon,
  className,
  ...rest
}: MTileProps) {
  const valueColor =
    tone === "crit"
      ? "text-[color:var(--m-crit)]"
      : tone === "warn"
        ? "text-[color:var(--m-warn)]"
        : "text-[color:var(--m-ink)]";
  return (
    <button
      type="button"
      className={cn(
        "m-card p-3 text-left tap-feedback transition hover:border-[color:var(--m-line-2)]",
        className,
      )}
      {...rest}
    >
      <div className={cn("text-[23px] font-bold leading-none tracking-tight", valueColor)}>
        {value}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[11.5px] font-medium text-[color:var(--m-gray)]">
        {icon}
        <span>{label}</span>
      </div>
    </button>
  );
}

export type MStatusTone = "ok" | "warn" | "crit" | "info" | "indigo" | "neutral";

const toneMap: Record<MStatusTone, { fg: string; bg: string; border: string; dot: string }> = {
  ok: {
    fg: "text-[color:var(--m-ok)]",
    bg: "bg-[color:var(--m-ok-bg)]",
    border: "border-[color:var(--m-ok)]/20",
    dot: "bg-[color:var(--m-ok)]",
  },
  warn: {
    fg: "text-[color:var(--m-warn)]",
    bg: "bg-[color:var(--m-warn-bg)]",
    border: "border-[color:var(--m-warn)]/20",
    dot: "bg-[color:var(--m-warn)]",
  },
  crit: {
    fg: "text-[color:var(--m-crit)]",
    bg: "bg-[color:var(--m-crit-bg)]",
    border: "border-[color:var(--m-crit)]/20",
    dot: "bg-[color:var(--m-crit)]",
  },
  info: {
    fg: "text-[color:var(--m-info)]",
    bg: "bg-[color:var(--m-info-bg)]",
    border: "border-[color:var(--m-info)]/20",
    dot: "bg-[color:var(--m-info)]",
  },
  indigo: {
    fg: "text-[color:var(--m-indigo)]",
    bg: "bg-[color:var(--m-indigo-bg)]",
    border: "border-[color:var(--m-indigo)]/20",
    dot: "bg-[color:var(--m-indigo)]",
  },
  neutral: {
    fg: "text-[color:var(--m-gray)]",
    bg: "bg-[color:var(--m-fill-2)]",
    border: "border-[color:var(--m-line)]",
    dot: "bg-[color:var(--m-gray)]",
  },
};

export function MStatusPill({
  tone = "neutral",
  children,
  className,
  withDot = true,
}: {
  tone?: MStatusTone;
  children: ReactNode;
  className?: string;
  withDot?: boolean;
}) {
  const t = toneMap[tone];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold",
        t.fg,
        t.bg,
        t.border,
        className,
      )}
    >
      {withDot && <i className={cn("h-1.5 w-1.5 rounded-full", t.dot)} />}
      {children}
    </span>
  );
}

export function MSegmented<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode }>;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        "flex rounded-xl border border-[color:var(--m-line)] bg-[color:var(--m-fill)] p-0.5",
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              "flex-1 rounded-[10px] px-3 py-2 text-[11.5px] font-semibold transition-colors",
              active
                ? "bg-white text-[color:var(--m-ink)] shadow-[0_1px_2px_rgba(16,16,20,0.08)]"
                : "text-[color:var(--m-gray)]",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function MPill({
  active,
  onClick,
  children,
  count,
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "whitespace-nowrap rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors",
        active
          ? "border-transparent bg-[color:var(--m-ink)] text-white"
          : "border-[color:var(--m-line)] bg-white text-[color:var(--m-gray)]",
      )}
    >
      {children}
      {typeof count === "number" && (
        <span className={cn("ml-1 opacity-60")}>{count}</span>
      )}
    </button>
  );
}

export function MSearchField({
  value,
  onChange,
  placeholder = "Search",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex items-center gap-2 rounded-xl border border-[color:var(--m-line)] bg-[color:var(--m-fill)] px-3 py-2.5 text-[color:var(--m-gray-3)]">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="h-4 w-4"
        aria-hidden
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-[13px] text-[color:var(--m-ink)] outline-none placeholder:text-[color:var(--m-gray-3)]"
      />
    </label>
  );
}

export function MSectionLabel({
  children,
  className,
  action,
}: {
  children: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  return (
    <div className={cn("mt-4 mb-2 flex items-center justify-between px-1", className)}>
      <span className="m-section-label">{children}</span>
      {action}
    </div>
  );
}

export function MEmpty({
  title,
  description,
  icon,
}: {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="m-card flex flex-col items-center justify-center gap-2 p-6 text-center">
      {icon && (
        <div className="grid h-10 w-10 place-items-center rounded-full bg-[color:var(--m-fill-2)] text-[color:var(--m-gray)]">
          {icon}
        </div>
      )}
      <p className="text-[14px] font-semibold text-[color:var(--m-ink)]">{title}</p>
      {description && (
        <p className="text-[12px] text-[color:var(--m-gray-2)]">{description}</p>
      )}
    </div>
  );
}

export function MBackBar({
  title,
  onBack,
  right,
}: {
  title: string;
  onBack: () => void;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 px-1 pb-3 pt-1">
      <button
        type="button"
        onClick={onBack}
        aria-label="Back"
        className="grid h-8 w-8 place-items-center rounded-[10px] border border-[color:var(--m-line)] bg-white text-[color:var(--m-gray)] tap-feedback"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
          <path d="m15 18-6-6 6-6" />
        </svg>
      </button>
      <h2 className="text-[15px] font-semibold text-[color:var(--m-ink)]">{title}</h2>
      <div className="ml-auto">{right}</div>
    </div>
  );
}
